import { IMeetingAnalysis, IPendingWrapUp } from '../../../services/crm';

// #region wrapup.helpers
// Pure helpers for the wrap-up card: the draft the rep edits, and due-date chips.

export type DueChoice = 'none' | 'tomorrow' | 'three_days' | 'next_week';

export interface DraftTask {
    key: string;
    text: string;
    owner: 'rep' | 'prospect';
    due: DueChoice;
    on: boolean;
}

export interface DraftNote {
    key: string;
    text: string;
    on: boolean;
}

/** Who the call was with, when the CRM does not know yet. Becomes (or matches) a lead on wrap-up. */
export interface DraftPerson {
    name: string;
    email: string;
    company: string;
}

export interface WrapUpDraft {
    summary: string;
    /** Null when a lead is already attached. */
    person: DraftPerson | null;
    notes: DraftNote[];
    tasks: DraftTask[];
    outcome: 'completed' | 'no_show' | null;
}

export const DUE_LABELS: Record<DueChoice, string> = { none: 'No date', tomorrow: 'Tomorrow', three_days: 'In 3 days', next_week: 'Next week' };

/** 9:00 local on the chosen day; next week = the coming Monday. */
export const dueAt = (choice: DueChoice, now = new Date()): string | null => {
    if (choice === 'none') return null;
    const d = new Date(now);
    d.setHours(9, 0, 0, 0);
    if (choice === 'tomorrow') d.setDate(d.getDate() + 1);
    else if (choice === 'three_days') d.setDate(d.getDate() + 3);
    else { d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7)); }
    return d.toISOString();
};

const dueFromDays = (days: number | null): DueChoice => (days === null ? 'none' : days <= 1 ? 'tomorrow' : days <= 4 ? 'three_days' : 'next_week');

/** The model's proposal as an editable draft: rep items on, prospect items off, notes and objections all on. */
export const draftFrom = (w: IPendingWrapUp): WrapUpDraft => {
    const a: IMeetingAnalysis | null = w.analysis;
    const past = w.appointment ? Date.parse(w.appointment.end_at) < Date.now() : true;
    return {
        summary: a?.summary ?? w.transcript_excerpt,
        person: w.lead ? null : {
            name: w.appointment?.invitee_name || a?.prospect?.name || '',
            email: w.appointment?.invitee_email || '',
            company: a?.prospect?.company || ''
        },
        notes: [
            ...(a?.key_points ?? []).map((text, i) => ({ key: `k${i}`, text, on: true })),
            ...(a?.objections ?? []).map((text, i) => ({ key: `o${i}`, text: `Objection: ${text}`, on: true })),
            ...(a?.decisions ?? []).map((text, i) => ({ key: `d${i}`, text: `Agreed: ${text}`, on: true }))
        ],
        tasks: (a?.action_items ?? []).map((it, i) => ({ key: `t${i}`, text: it.text, owner: it.owner, due: dueFromDays(it.due_in_days), on: it.owner === 'rep' })),
        outcome: w.appointment && w.appointment.status === 'scheduled' && past ? 'completed' : null
    };
};

export const SENTIMENT: Record<'positive' | 'neutral' | 'negative', { label: string; cls: string }> = {
    positive: { label: 'Went well', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
    neutral: { label: 'Neutral', cls: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-300' },
    negative: { label: 'Needs care', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' }
};

export const whenLabel = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const day = new Date(d); day.setHours(0, 0, 0, 0);
    const diff = Math.round((today.getTime() - day.getTime()) / 86_400_000);
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return diff === 0 ? `Today, ${time}` : diff === 1 ? `Yesterday, ${time}` : `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}, ${time}`;
};
